import { Typography } from '@euler/app/components/typography';

import { SearchNavHeader } from '@euler/app/flows/task/components/SearchNavHeader';
import { useHandleConstructionWorkItemPress } from '@euler/app/flows/task/pages/construction/functions/useHandleConstructionWorkItemPress';
import { Center } from '@euler/components/Center';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { countExactMatches } from '@euler/utils/countExactMatches';
import { useBehaviorSubject, useCreation } from '@euler/utils/hooks';
import { StackNavigationOptions } from '@react-navigation/stack';
import Fuse, { FuseResultMatch } from 'fuse.js';
import { memo, useCallback, useMemo, useState } from 'react';
import { FlatList, KeyboardAvoidingView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from 'styled-components';
import {
  useConstructionJobScheduleContext,
  useScheduleConstructionJobs,
} from './ConstructionJobScheduleContext';
import { WorkItemCell } from './components/WorkItemCell';
import { ConstructionWorkItemInfo } from './functions/useConstructionWorkItems';

const Cell = memo(
  ({
    item,
    matches,
    selected,
    onPress,
    last,
  }: {
    first?: boolean;
    last?: boolean;
    item: ConstructionWorkItemInfo;
    selected: boolean;
    matches?: ReadonlyArray<FuseResultMatch>;
    onPress: (item: ConstructionWorkItemInfo) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const {
      construction: {
        schedule: {
          workItem: {
            cell: { textLight, bgLight },
          },
        },
      },
    } = useTheme();

    const onCellPress = useCallback(() => {
      onPress?.(item);
    }, [item, onPress]);

    return (
      <WorkItemCell
        title={item.name}
        matches={matches}
        selected={selected}
        onPress={onCellPress}
        onAdd={onCellPress}
        onRemove={onCellPress}
        titleStyle={{
          color: textLight,
          fontFamily: FontFamily.NotoSans.Light,
          fontSize: rpx(15),
        }}
        css={`
          background-color: ${bgLight};
          padding-top: ${rpx(5)}px;
          padding-bottom: ${rpx(5)}px;
          padding-left: ${rpx(15)}px;
          padding-right: ${rpx(15)}px;
          margin: 0;
          border-bottom-width: 1px;
          border-bottom-color: ${last ? 'transparent' : '#eee'};
          border-radius: 0;
        `}
      />
    );
  },
);

export const WorkItemSearchScreen = wrapNavigatorScreen(
  () => {
    const insets = useSafeAreaInsets();
    const { rpx } = useResponsiveContext();
    const [searchText, setSearchText] = useState<string>();

    const { categories$ } = useConstructionJobScheduleContext();
    const [categories] = useBehaviorSubject(categories$);

    const { selectedWorkItems } = useScheduleConstructionJobs();

    const workItems = useMemo(
      () => categories.flatMap(x => x.groups.flatMap(g => g.items)),
      [categories],
    );

    const fuse = useCreation(() => {
      return new Fuse(workItems, {
        keys: ['name', 'pyInitials'],
        includeMatches: true,
        includeScore: true,
        threshold: 0.6,
        ignoreLocation: true,
      });
    }, [workItems]);

    const onSearchInputTextChange = useCallback((text: string) => {
      setSearchText(text);
    }, []);

    const matchedResults = useMemo(() => {
      const keyword = searchText?.trim();
      if (!keyword) return [];
      const results = fuse.search(keyword).map(result => {
        const normalizedFuseScore = result.score ? 1 - result.score : 0;
        const nameMatch = result.matches?.find(x => x.key === 'name');
        const pyMatch = result.matches?.find(x => x.key === 'pyInitials');

        if (nameMatch == null) {
          if (pyMatch == null) {
            return {
              ...result,
              exactMatches: 0,
              customScore: normalizedFuseScore,
            };
          }
          const searchValue = result.item.pyInitials;
          const exactMatches = countExactMatches(searchValue, keyword);
          return {
            ...result,
            exactMatches,
            customScore: exactMatches * 0.6 + normalizedFuseScore * 0.4,
          };
        } else {
          const searchValue = result.item.name;
          const exactMatches = countExactMatches(searchValue, keyword);
          return {
            ...result,
            exactMatches,
            customScore: exactMatches * 0.6 + normalizedFuseScore * 0.4,
          };
        }
      });
      return results.sort((a, b) => {
        if (Math.abs(a.customScore - b.customScore) < 0.001) {
          return b.exactMatches - a.exactMatches;
        }
        return b.customScore - a.customScore;
      });
    }, [fuse, searchText]);

    const onCellPress = useHandleConstructionWorkItemPress();

    return (
      <KeyboardAvoidingView
        behavior="padding"
        css={`
          flex: 1;
        `}
      >
        <SearchNavHeader
          searchInputEditable
          searchText={searchText}
          searchPlaceholder={'搜索施工项目'}
          onSearchInputChange={onSearchInputTextChange}
        />
        <FlatList
          keyboardShouldPersistTaps="handled"
          data={matchedResults}
          css={`
            flex: 1;
          `}
          contentContainerStyle={{
            flexGrow: 1,
            minHeight: 0,
            paddingBottom: Math.max(insets.bottom, rpx(15)),
          }}
          renderItem={({ item, index }) => (
            <Cell
              item={item.item}
              matches={item.matches}
              selected={selectedWorkItems.some(
                x => x.workItem.name === item.item.name,
              )}
              onPress={onCellPress}
              first={index === 0}
              last={index === matchedResults.length - 1}
            />
          )}
          ListHeaderComponent={
            !matchedResults.length ? (
              <Center
                css={`
                  padding: ${rpx(32)}px;
                  height: ${rpx(200)}px;
                `}
              >
                <Typography.Label light color="#555">
                  {searchText?.trim().length
                    ? '未找到施工项目? 试试调整搜索关键词，支持按拼音首字母或项目中文名称搜索'
                    : '支持按拼音首字母或项目中文名称搜索'}
                </Typography.Label>
              </Center>
            ) : null
          }
        />
      </KeyboardAvoidingView>
    );
  },
  {
    cardOverlayEnabled: true,
    cardStyle: { backgroundColor: 'white' },
    // animationEnabled: false,
    headerShown: false,
    // gestureEnabled: false,
  } as StackNavigationOptions,
);
